export interface Usuario {
  usuarioId: string;
  nome: string;
  email: string;
  role: 'owner' | 'admin' | 'gerente' | 'atendente' | 'super_admin';
  organizationId?: string;
  /** Conta principal (atendimento, transferência de lead) */
  whatsappAccountId?: string | null;
  /** Conta para campanha / disparos (opcional; deve ser diferente da principal se ambos preenchidos) */
  whatsappAccountIdCampanha?: string | null;
  /** Se preenchido (atendentes), o gerente associado enxerga as campanhas deste usuário. */
  gerenteId?: string | null;
  podeVerBacklog?: boolean; // Permite atendente ver backlog (default: false)
  /**
   * Toggle "Atender com IA" per-usuário. Quando `true` e
   * `IA_USER_TOGGLE_ENABLED=true` no backend, todo lead novo atribuído a
   * este usuário nasce em `atendimentoModo='ia'`. Cf.
   * `map-funnel-stage-and-add-ia-toggle`.
   */
  aiAutoAttendEnabled?: boolean;
  ativo: boolean;
  createdAt: string;
  updatedAt: string;
}

export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'trialing' | 'incomplete';
export type BillingCycle = 'monthly' | 'yearly';

export interface TrialInfo {
  isActive: boolean;
  startDate: string;
  endDate: string;
  maxUsers: number;
  maxLeadsPerMonth: number;
  maxWhatsAppAccounts: number;
  activatedBy: string;
  activatedAt: string;
}

export interface DashboardGoals {
  leadsReceivedMonth: number;
  leadsAttendedMonth: number;
  leadsConvertedMonth: number;
  conversionRatePercent: number;
  firstResponseTimeMinutes: number;
  maxBacklog: number;
}

export interface DashboardStatsPeriod {
  leadsReceived: number;
  leadsAttended: number;
  leadsConverted: number;
  conversionRate: number;
}

export interface DashboardTrendPoint {
  value: number;
  date?: string;
  month?: string;
}

export interface DashboardStatsByUser {
  usuarioId: string;
  nome: string;
  today: { leadsAttended: number; leadsConverted: number };
  yesterday: { leadsAttended: number; leadsConverted: number };
  month: { leadsAttended: number; leadsConverted: number };
  avgFirstResponseMinutes: number;
}

export interface DashboardCampaignMetrics {
  unsubscribesToday: number;
  unsubscribesMonth: number;
  dispatchesSentToday: number;
  dispatchesSentMonth: number;
}

export interface DashboardPipelineNow {
  emNegociacao: number;
  emProposta: number;
}

export interface DashboardStatsResponse {
  today: DashboardStatsPeriod;
  yesterday: DashboardStatsPeriod;
  month: DashboardStatsPeriod;
  year: DashboardStatsPeriod;
  trend: {
    dailyLeadsReceived: DashboardTrendPoint[];
    monthlyLeadsReceived: DashboardTrendPoint[];
  };
  backlogCount: number;
  avgFirstResponseMinutes: number;
  goals: DashboardGoals;
  timezone: string;
  lastUpdatedAt: string;
  byUser?: DashboardStatsByUser[];
  /** YYYY-MM — período dos blocos Mês, Dia e métricas mensais por atendente */
  referenceMonth: string;
  /** Ano civil do gráfico de barras mensais */
  chartYear: number;
  /** Stock actual na org (Kanban vendas) */
  pipelineNow?: DashboardPipelineNow;
  campaignMetrics?: DashboardCampaignMetrics;
}

export type UnsubscribeSource = 'link_click' | 'manual_admin' | 'import';

export interface UnsubscribedPhoneRow {
  unsubscribeId: string;
  organizationId: string;
  phoneNormalized: string;
  source: UnsubscribeSource;
  reason?: string;
  campaignItemId?: string;
  /** Snapshot gravado no opt-out (backend pode enviar junto com contactNome/campaignNome) */
  linkedContactNome?: string;
  linkedCampaignId?: string;
  linkedCampaignNome?: string;
  createdAt: string;
  updatedAt: string;
  createdByIp?: string;
  userAgent?: string;
  revokedAt?: string;
  revokedByUsuarioId?: string;
  revokedByNome?: string;
  revokedReason?: string;
  contactNome?: string;
  contactTelefone?: string;
  campaignId?: string;
  campaignNome?: string;
}

export interface ListUnsubscribedPhonesResponse {
  items: UnsubscribedPhoneRow[];
  total: number;
  nextPageToken?: string;
}

export interface BulkDeleteUnsubscribedResponse {
  ok?: boolean;
  deleted: string[];
  notFound: string[];
  forbidden: string[];
  message?: string;
}

export type PosVendasFinalizadoInboundRoute =
  | 'vendas_aguardando_contato'
  | 'pos_vendas_em_atendimento';

export interface PosVendasSettings {
  finalizadoInboundRoute?: PosVendasFinalizadoInboundRoute;
}

export interface KanbanVendasColumnLabels {
  backlog?: string;
  aguardandoContato?: string;
  emAtendimento?: string;
  emNegociacao?: string;
  urgentes?: string;
  /** @deprecated Preferir `urgentes`. */
  inativos?: string;
  proposta?: string;
  fechamento?: string;
}

export interface KanbanPosVendasColumnLabels {
  conversoesGanhas?: string;
  emAtendimento?: string;
  aguardandoRetorno?: string;
  finalizado?: string;
}

export interface KanbanCampanhasColumnLabels {
  pendente?: string;
  emExecucao?: string;
  finalizado?: string;
}

export interface KanbanColumnLabels {
  vendas?: KanbanVendasColumnLabels;
  posVendas?: KanbanPosVendasColumnLabels;
  campanhas?: KanbanCampanhasColumnLabels;
}

export interface Organization {
  organizationId: string;
  name: string;
  slug: string;
  timezone?: string;
  planId: string;
  plan?: Plan;
  ownerId: string;
  status: 'active' | 'suspended' | 'cancelled';
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: SubscriptionStatus;
  unsubscribeSettings?: UnsubscribeSettings;
  posVendasSettings?: PosVendasSettings;
  kanbanColumnLabels?: KanbanColumnLabels;
  trial?: TrialInfo;
  dashboardGoals?: DashboardGoals;
  /** Visualização do Funil IA: physics (bolinhas animadas) ou slides (cards). Default: physics. */
  funilVisualizacao?: 'physics' | 'slides';
  /** Configurações de IA persistidas (flags de funil, orquestrador, etc.). */
  aiSettings?: AISettings;
  /** Meses para próximo contato após sucesso em campanha (lost card). */
  defaultRemarketingMeses?: number | null;
  usersCount?: number;
  leadsThisMonth?: number;
  createdAt: string;
  updatedAt: string;
}

export interface UnsubscribeSettings {
  enabled?: boolean;
  baseUrl?: string;
  footerTemplate?: string;
}

export interface Plan {
  planId: string;
  name: string;
  description?: string;
  priceMonthly: number;
  priceYearly: number;
  maxUsers: number;
  maxLeadsPerMonth: number;
  maxWhatsAppAccounts: number;
  features?: string[];
}

export type AtendimentoModo = 'ia' | 'manual';
export type IaRunState = 'idle' | 'running' | 'needs_human' | 'awaiting_human_send';

export interface PendingIaDraft {
  conteudo: string;
  kind: 'preco' | 'texto';
  fonteResumo?: string;
  createdAt: string;
  expiresAt?: string;
}

export type IaHandoffBin = 'human' | 'inactive' | 'lowintent' | 'optout' | 'toxic';

export interface IaOrchestratorSignal {
  source: string;
  contract?: string;
  confidence?: number;
  reason?: string;
}

/** Contexto acumulado entre turnos (nome do curso, intenção de preço na última mensagem). */
export interface IaConversationContext {
  nomeCurso?: string;
  solicitouPrecoNaUltima?: boolean;
  apresentacaoFeita?: boolean;
  classificadorAtualizadoEm?: string;
  orchestratorFinalDecision?: string;
  orchestratorSignals?: IaOrchestratorSignal[];
  handoffBin?: IaHandoffBin;
  orchestratorInboundTurnCount?: number;
  orchestratorDecisionAt?: string;
  archivedByLowIntentPolicyAt?: string;
  lowIntentBadgePending?: boolean;
  /** Cron `add-lost-lead-reactivation-cron` reabriu o lead. Frontend
   * mostra badge "Reativado" no card. */
  reactivatedFromLostLeadAt?: string;
}

export interface Lead {
  leadId: string;
  nome: string;
  telefone: string;
  cpf?: string;
  whatsappNumeroEntrada?: string;
  email: string;
  produtoInteresse: string;
  atendenteId: string;
  atendenteNome?: string;
  status:
    | 'backlog'
    | 'aguardando_contato'
    | 'em_atendimento'
    | 'em_negociacao'
    | 'proposta'
    | 'fechamento'
    | 'urgente'
    | 'excluido';
  statusFechamento?: 'em_andamento' | 'vendido' | 'nao_vendido' | 'inativo' | null;
  posVendaStatus?: 'conversoes_ganhas' | 'em_atendimento' | 'aguardando_retorno' | 'finalizado' | null;
  interessadoLigarEmMeses?: number | null;
  interessadoLigarEmData?: string | null;
  contatoRh?: boolean;
  nomeEmpresa?: string;
  observacao?: string;
  anexos: Anexo[];
  unreadCount?: number;
  clienteId?: string | null;
  createdAt: string;
  updatedAt: string;
  ultimaMensagemAt?: string | null;
  ultimaMensagemOutboundAt?: string | null;
  ultimaMensagemInboundAt?: string | null;
  atendimentoModo?: AtendimentoModo;
  iaSessionId?: string;
  iaRunState?: IaRunState;
  pendingIaDraft?: PendingIaDraft;
  iaLastIdempotencyKey?: string;
  iaConversationContext?: IaConversationContext;
  inboundCampaignId?: string;
  aiFunnelStage?: 'inicio' | 'em_negociacao_ia' | 'proposta_ia';
  aiFunnelUpdatedAt?: string;
  aiFunnelStageEnteredAt?: string;
  /** Handoff Funil IA → Kanban humano (persistido). */
  aiFunnelHandoffAt?: string;
  /** Sinal de reativação detectado no inbound pelo classificador IA. */
  isReativacao?: boolean;
  reativacaoDetectedAt?: string;
  reativacaoSource?: 'llm' | 'manual';
  reativacaoMotivo?: string;
  reativacaoConfidence?: number;
}

export type CampaignAttendanceMode = 'human_kanban' | 'ai_funnel';

/** Kanban de Campanhas */
export type CampaignStatus = 'rascunho' | 'pendente' | 'ativa' | 'encerrada';
export type CampaignItemPipelineStage =
  | 'pendente'
  | 'disparado'
  | 'sucesso'
  | 'perdido'
  | 'encerrado';
export type CampaignItemDisparoStatus = 'pending' | 'sent' | 'failed' | 'skipped';
export type CampaignItemStatus = 'pendente' | 'disparado' | 'nao_disparado';
export type CampaignItemReason =
  | 'optout'
  | 'posvendas'
  | 'perdido'
  | 'duplicado_mesma_campanha'
  | 'invalido';

export type CampaignItemSkipReason = 'unsubscribed' | 'pos_vendas_active';
export type CampaignItemSourceType = 'lost_card' | 'partner' | 'imported_lead' | 'csv_transient';
export type CampaignOrigin = 'lost_leads' | 'partners' | 'imported_leads' | 'mixed';

export interface Campaign {
  campaignId: string;
  organizationId: string;
  nome: string;
  mensagem: string;
  whatsappAccountId?: string;
  principalWhatsappUsuarioId?: string;
  principalCallBlockEnabled?: boolean;
  principalCallIntroText?: string;
  /**
   * Atendente do «Ligue para» pode disparar e ver no Kanban após haver contactos (definido por admin/gerente).
   * Não inclui edição na Gestão de Campanhas.
   */
  delegateManagementToPrincipalMember?: boolean;
  /** Contagem de itens na campanha (API). */
  campaignItemCount?: number;
  /** Fluxo completo na Gestão de Campanhas para o utilizador autenticado. */
  viewerCanManage?: boolean;
  /** Ver Kanban, Criar Disparo e mover cartões para o utilizador autenticado. */
  viewerCanDispatch?: boolean;
  unsubscribeUseGlobal?: boolean;
  unsubscribeOverrideEnabled?: boolean;
  unsubscribeBaseUrl?: string;
  unsubscribeFooterTemplate?: string;
  /** true = permitir disparo com telefone em pós-vendas ativo na org */
  allowActivePosVendasDispatch?: boolean;
  /**
   * Atribuição de leads inbound da campanha. MVP entrega só `single`.
   * Cf. `add-campaign-lead-assignment`.
   */
  assignmentMode?: 'single' | 'distribute';
  assignedAtendentes?: { usuarioId: string; whatsappAccountIdOverride?: string }[];
  /** Preferências para o primeiro disparo no Kanban (merge com defaults globais). */
  defaultDisparoIntervaloMinutos?: number;
  defaultDisparoMaxDisparosPorNumero?: number;
  defaultDisparoHoraInicio?: string;
  defaultDisparoHoraFim?: string;
  defaultDisparoAutoResumeOnMax?: boolean;
  /** Omitido = fluxo legado (Kanban humano de campanha). */
  attendanceMode?: CampaignAttendanceMode;
  status: CampaignStatus;
  origem: CampaignOrigin;
  createdByUsuarioId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CampaignItem {
  campaignItemId: string;
  organizationId: string;
  campaignId: string;
  pipelineStage: CampaignItemPipelineStage;
  nome: string;
  telefone: string;
  phoneNormalized: string;
  sourceType: CampaignItemSourceType;
  sourceId?: string;
  sourceLeadId?: string;
  messageSnapshot: string;
  interessadoLigarEmMeses?: number;
  disparoStatus?: CampaignItemDisparoStatus;
  skipReason?: CampaignItemSkipReason;
  status?: CampaignItemStatus;
  reason?: CampaignItemReason;
  disparadoAt?: string;
  repliedAt?: string;
  importedUltimoCampanhaEmBeforeCampaign?: string;
  createdAt: string;
  updatedAt: string;
}

export interface SkippedUnsubscribedContact {
  sourceId?: string;
  nome: string;
  telefone: string;
}

export interface AddCampaignItemsApiResult {
  items: CampaignItem[];
  skippedUnsubscribed: SkippedUnsubscribedContact[];
}

export interface CsvTransientLeadInput {
  nome: string;
  telefone: string;
  phoneNormalized: string;
  cpf?: string;
  email?: string;
}

export interface AddCsvCampaignItemsApiResult {
  batchId: string;
  created: number;
  duplicate: number;
  invalid: number;
  failed: number;
  items: CampaignItem[];
}

export interface CreateCampaignRequest {
  nome: string;
  /** `rascunho`: só nome obrigatório; omitido ou `ativa` = fluxo completo legado. */
  status?: 'rascunho' | 'ativa';
  mensagem?: string;
  whatsappAccountId?: string;
  principalWhatsappUsuarioId?: string | null;
  principalCallBlockEnabled?: boolean;
  principalCallIntroText?: string | null;
  unsubscribeUseGlobal?: boolean;
  unsubscribeOverrideEnabled?: boolean;
  unsubscribeBaseUrl?: string;
  unsubscribeFooterTemplate?: string;
  delegateManagementToPrincipalMember?: boolean;
  allowActivePosVendasDispatch?: boolean;
  defaultDisparoIntervaloMinutos?: number;
  defaultDisparoMaxDisparosPorNumero?: number;
  defaultDisparoHoraInicio?: string;
  defaultDisparoHoraFim?: string;
  defaultDisparoAutoResumeOnMax?: boolean;
  attendanceMode?: CampaignAttendanceMode;
  assignmentMode?: 'single' | 'distribute';
  assignedAtendentes?: { usuarioId: string; whatsappAccountIdOverride?: string }[];
}

export interface UpdateCampaignRequest {
  nome?: string;
  mensagem?: string;
  status?: CampaignStatus;
  whatsappAccountId?: string | null;
  principalWhatsappUsuarioId?: string | null;
  principalCallBlockEnabled?: boolean;
  principalCallIntroText?: string | null;
  unsubscribeUseGlobal?: boolean;
  unsubscribeOverrideEnabled?: boolean;
  unsubscribeBaseUrl?: string;
  unsubscribeFooterTemplate?: string;
  delegateManagementToPrincipalMember?: boolean;
  allowActivePosVendasDispatch?: boolean;
  defaultDisparoIntervaloMinutos?: number;
  defaultDisparoMaxDisparosPorNumero?: number;
  defaultDisparoHoraInicio?: string;
  defaultDisparoHoraFim?: string;
  defaultDisparoAutoResumeOnMax?: boolean;
  attendanceMode?: CampaignAttendanceMode;
  assignmentMode?: 'single' | 'distribute' | null;
  assignedAtendentes?: { usuarioId: string; whatsappAccountIdOverride?: string }[] | null;
}

/** Notifications */
export type NotificationType =
  | 'lead_assigned'
  | 'pos_vendas_retorno'
  | 'ia_proposta'
  | 'ia_nao_configurada'
  | 'ia_remarketing'
  | 'lead_reactivation_due';

export interface AppNotification {
  notificationId: string;
  organizationId: string;
  targetUsuarioId: string;
  type: NotificationType;
  title: string;
  message: string;
  leadId?: string;
  read: boolean;
  createdAt: string;
  ttl: number;
}

export type LostCardStage = 'perdido' | 'em_campanha';

export type LostLeadCategory = 'perdido' | 'inativo';

export type LostLeadReason = 'low_intent' | 'non_intention' | 'idle_timeout';

export interface LostLeadCard {
  lostCardId: string;
  organizationId: string;
  dedupeKey: string;
  sourceLeadId: string;
  nome: string;
  cpf?: string;
  telefone?: string;
  whatsappNumeroEntrada?: string;
  observacao?: string;
  interessado: boolean;
  interessadoLigarEmMeses?: number | null;
  interessadoLigarEmData?: string | null;
  dataPerda: string;
  pipelineStage: LostCardStage;
  selecionado_backlog_perdidos: boolean;
  lostCategory?: LostLeadCategory;
  lostReason?: LostLeadReason;
  createdAt: string;
  updatedAt: string;
}

export interface Anexo {
  anexoId: string;
  s3Key: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
}

export interface Cliente {
  clienteId: string;
  nome: string;
  telefone: string;
  email?: string;
  documento?: string;
  endereco?: Endereco;
  dadosAdicionais?: ClienteDadosAdicionais;
  leads?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ClienteDadosAdicionais {
  tipoContato?: string;
  nomeContato?: string;
  nomeEmpresa?: string;
  criadoPor?: string;
  criadoPorNome?: string;
  emCampanhaAtiva?: boolean;
  ultimoCampanhaEm?: string;
  [key: string]: any;
}

export interface ImportedLead {
  importedLeadId: string;
  organizationId: string;
  dedupeKey: string;
  nome: string;
  telefone: string;
  cpf?: string;
  email?: string;
  emCampanha: boolean;
  ultimoCampanhaEm?: string;
  importedBy: string;
  importedByNome: string;
  createdAt: string;
  updatedAt: string;
}

// Disparos
export type DisparoStatus =
  | 'criado'
  | 'em_execucao'
  | 'pausado'
  | 'pausado_horario'
  | 'max_atingido'
  | 'concluido'
  | 'cancelado';

export interface Disparo {
  disparoId: string;
  organizationId: string;
  campaignId: string;
  whatsappAccountId: string;
  intervaloMinutos: number;
  proximoDisparoEm: string;
  horaInicio: string;
  horaFim: string;
  maxDisparosPorNumero: number;
  disparosRealizados: number;
  /** Quando true, ao atingir o limite reseta a contagem e continua automaticamente até não haver mais pendentes. */
  autoResumeOnMax?: boolean;
  status: DisparoStatus;
  createdBy: string;
  createdByNome: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDisparoRequest {
  campaignId: string;
  /** Opcional: vazio envia sem campo para o backend usar campanha → principal do perfil. */
  whatsappAccountId?: string;
  intervaloMinutos: number;
  horaInicio: string;
  horaFim: string;
  maxDisparosPorNumero: number;
  autoResumeOnMax?: boolean;
}

export interface PatchDisparoConfigRequest {
  horaInicio?: string;
  horaFim?: string;
  intervaloMinutos?: number;
  maxDisparosPorNumero?: number;
  autoResumeOnMax?: boolean;
}

export interface Endereco {
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
}

export interface Mensagem {
  messageId: string;
  leadId: string;
  timestamp: number;
  conteudo: string;
  direcao: 'inbound' | 'outbound';
  tipo: 'text' | 'image' | 'document' | 'audio';
  anexoUrl?: string | null;
  mediaUrl?: string | null;
  s3Key?: string;
  enviadoPor: string;
  lida: boolean;
  createdAt: string;
  origemIa?: boolean;
}

export interface WhatsAppAccount {
  accountId: string;
  nome: string;
  sessionId?: string | null;
  telefone: string;
  status: 'conectado' | 'desconectado' | 'erro' | 'aguardando_qr';
  config?: Record<string, any>;
  createdAt: string;
  updatedAt: string;
  ultimaConexaoAt?: string | null;
}

export interface LoginRequest {
  email: string;
  senha: string;
}

export interface LoginResponse {
  token: string;
  user: Usuario;
  organization?: Organization;
}

export interface ApiResponse<T> {
  data?: T;
  error?: string;
  message?: string;
}

export interface ListResponse<T> {
  [key: string]: T[] | number | undefined;
  total: number;
  leads?: T[];
  mensagens?: T[];
  clientes?: T[];
  usuarios?: T[];
  accounts?: T[];
}

// Organization API Types
export interface RegisterRequest {
  nome: string;
  email: string;
  senha: string;
  organizationName: string;
  organizationSlug: string;
  planId: string;
}

export interface RegisterResponse {
  token: string;
  user: Usuario;
  organization: Organization;
}

export interface OrganizationUsersResponse {
  users: OrganizationUser[];
  total: number;
  maxUsers: number;
}

export interface OrganizationUser extends Usuario {
  inviteStatus?: 'active' | 'pending';
  inviteId?: string;
}

export interface InviteUserRequest {
  email: string;
  role: 'admin' | 'gerente' | 'atendente';
}

export interface InviteUserResponse {
  message: string;
  invite: {
    inviteId: string;
    email: string;
    nome?: string;
    role: string;
    organizationId: string;
    status: 'pending' | 'accepted' | 'expired';
    expiresAt: string;
    createdAt: string;
  };
}

export interface UpdateOrganizationRequest {
  name?: string;
  slug?: string;
  timezone?: string;
  unsubscribeSettings?: UnsubscribeSettings;
  /** Funil IA no Dashboard: bolinhas animadas ou cartões por estágio. */
  funilVisualizacao?: 'physics' | 'slides';
}

export interface ChangePlanRequest {
  planId: string;
}

export interface OrganizationUsageStats {
  usersCount: number;
  maxUsers: number;
  leadsThisMonth: number;
  maxLeadsPerMonth: number;
  whatsappAccounts: number;
  maxWhatsAppAccounts: number;
}

export interface PlansResponse {
  plans: Plan[];
}

// Billing/Subscription Types
export interface Subscription {
  subscriptionId: string;
  organizationId: string;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  stripePriceId: string;
  planId: string;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCheckoutRequest {
  planId: string;
  billingCycle: BillingCycle;
  successUrl?: string;
  cancelUrl?: string;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string;
}

export interface CustomerPortalResponse {
  url: string;
}

export interface SubscriptionResponse {
  subscription: Subscription | null;
}

// API Error Types
export type LimitType = 'users' | 'leads' | 'whatsapp';

export interface ApiErrorResponse {
  error: string;
  message: string;
  code?: 'PAYMENT_REQUIRED' | 'PLAN_LIMIT_REACHED';
  limitType?: LimitType;
}

export function isPaymentRequiredError(error: any): boolean {
  return error?.response?.status === 402 ||
         error?.response?.data?.code === 'PAYMENT_REQUIRED';
}

export function isPlanLimitError(error: any): boolean {
  return error?.response?.data?.code === 'PLAN_LIMIT_REACHED';
}

export function getErrorMessage(error: any): string {
  const fromApi = error?.response?.data?.message;
  if (fromApi) return fromApi;

  const raw = typeof error?.message === 'string' ? error.message : '';
  const lower = raw.toLowerCase();
  // Axios uses "Network Error" when there is no HTTP response (CORS, dropped connection, gateway/Lambda timeout without body).
  if (raw === 'Network Error' || lower.includes('network error')) {
    return 'Não foi possível conectar à API (rede interrompida ou tempo esgotado). Tente de novo em instantes; se persistir, confira o deploy e os logs do Lambda.';
  }
  if (lower.includes('timeout') || error?.code === 'ECONNABORTED') {
    return 'Tempo da requisição esgotado. Tente novamente.';
  }

  return raw || 'Ocorreu um erro inesperado';
}

export function getLimitType(error: any): LimitType | null {
  return error?.response?.data?.limitType || null;
}

// -----------------------------------------------------------------------------
// AI Assistant Types
// -----------------------------------------------------------------------------

export type AIProvider = 'openai' | 'azure' | 'anthropic' | 'google' | 'bedrock';

// OpenSpec #2.7 — per-tier overrides
export type ModelTier = 'fast' | 'standard' | 'quality';
export interface TierConfig {
  provider: AIProvider;
  model?: string;
}
export interface ProvidersByTier {
  fast?: TierConfig;
  standard?: TierConfig;
  quality?: TierConfig;
}
export type AITom = 'formal' | 'casual' | 'tecnico' | 'amigavel' | 'profissional';
export type SuggestionTipo = 'direta' | 'acolhedora' | 'consultiva';
export type GuidelineTipo = 'atendimento' | 'curso' | 'produto' | 'preco' | 'servico';

export interface AISettings {
  enabled: boolean;
  provider: AIProvider;
  model?: string;
  temperature?: number;
  maxTokens?: number;
  defaultRoleId?: string;
  defaultTom?: AITom;
  /** OpenSpec #2.7 — per-tier overrides; quando ausente, fallback to provider/model legados. */
  providersByTier?: ProvidersByTier;
  /** Roteiro SDR: prioridade sobre secções se preenchido. */
  sdrScript?: string;
  sdrSections?: Record<string, string>;
  /** Mín. turnos inbound para o backend aceitar perda por baixa intenção; omitir = sem gate. */
  orchestratorLowIntentMinInboundTurns?: number | null;
  /** Coluna de handoff (bins) no Funil IA physics; flag `handoff_bins_ui` (F4.5). */
  handoffBinsUi?: boolean;
}

export interface FewShotExample {
  contexto: string;
  perguntaCliente: string;
  respostaSugerida: string;
}

export interface AIRole {
  roleId: string;
  organizationId: string;
  nome: string;
  descricao: string;
  systemPrompt: string;
  exemplos: FewShotExample[];
  tom: AITom;
  isDefault: boolean;
  isTemplate: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AIRoleTemplate {
  roleId: string;
  nome: string;
  descricao: string;
  tom: AITom;
  systemPrompt: string;
  exemplos: FewShotExample[];
}

export interface AISuggestion {
  id: number;
  tipo: SuggestionTipo;
  texto: string;
  confianca: number;
}

export interface AISuggestionResponse {
  suggestions: AISuggestion[];
  reasoning: string;
}

export interface Guideline {
  guidelineId: string;
  organizationId: string;
  titulo: string;
  conteudo: string;
  tipo: GuidelineTipo;
  sourceFile?: string;
  s3Key?: string;
  indexed: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateGuidelineRequest {
  titulo: string;
  conteudo?: string;
  tipo?: GuidelineTipo;
  sourceFile?: string;
  s3Key?: string;
}

// -----------------------------------------------------------------------------
// CATÁLOGO — OpenSpec add-categorized-catalog-guideline
// Espelha backend types pra MVP front-end (PR 2 Fase 3).
// -----------------------------------------------------------------------------

export type CatalogItemType = 'produto' | 'servico';
export type CatalogItemStatus = 'active' | 'inactive' | 'draft';
export type CatalogSectionType =
  | 'overview'
  | 'features'
  | 'pricing'
  | 'integrations'
  | 'faq';

export interface CatalogFaqEntry {
  question: string;
  answer: string;
}

export interface CatalogItem {
  organizationId: string;
  productId: string;
  type: CatalogItemType;
  categoryId: string;
  categoryLabel: string;
  name: string;
  status: CatalogItemStatus;
  descriptionShort: string;
  descriptionLong: string;
  attributes: Record<string, unknown>;
  faq?: CatalogFaqEntry[];
  integrations?: string[];
  pineconeVectorIds: string[];
  pineconeIndexedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCatalogItemRequest {
  type: CatalogItemType;
  categoryId: string;
  categoryLabel: string;
  name: string;
  status?: CatalogItemStatus;
  descriptionShort?: string;
  descriptionLong?: string;
  attributes?: Record<string, unknown>;
  faq?: CatalogFaqEntry[];
  integrations?: string[];
}

export type UpdateCatalogItemRequest = Partial<CreateCatalogItemRequest>;

export interface CatalogAskSource {
  productId: string;
  name: string;
  sectionType: CatalogSectionType;
  score: number;
}

export interface CatalogAskRequest {
  query: string;
  categoryId?: string;
  topK?: number;
}

export interface CatalogAskResponse {
  answer: string;
  sources: CatalogAskSource[];
}

export interface CreateAIRoleRequest {
  nome: string;
  descricao: string;
  systemPrompt: string;
  exemplos?: FewShotExample[];
  tom?: AITom;
  isDefault?: boolean;
  templateId?: string;
}

export type PriceSourceTipo = 'website' | 'guideline';

export interface PriceSource {
  priceSourceId: string;
  organizationId: string;
  nome: string;
  ativo: boolean;
  prioridade: number;
  tipo: PriceSourceTipo;
  allowedHosts?: string[];
  pathPrefixes?: string[];
  baseUrl?: string;
  searchUrlTemplate?: string;
  guidelineId?: string;
  notasInternas?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePriceSourceRequest {
  nome: string;
  ativo?: boolean;
  prioridade?: number;
  tipo: PriceSourceTipo;
  allowedHosts?: string[];
  pathPrefixes?: string[];
  baseUrl?: string;
  searchUrlTemplate?: string;
  guidelineId?: string;
  notasInternas?: string;
}

export interface RagWebPage {
  pageId: string;
  organizationId: string;
  nome: string;
  url: string;
  allowedHosts: string[];
  ativo: boolean;
  indexed?: boolean;
  lastIndexedAt?: string;
  lastIndexError?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateRagWebPageRequest {
  nome: string;
  url: string;
  allowedHosts: string[];
  ativo?: boolean;
}

export interface UpdateRagWebPageRequest {
  nome?: string;
  url?: string;
  allowedHosts?: string[];
  ativo?: boolean;
  indexed?: boolean;
  lastIndexedAt?: string;
  lastIndexError?: string;
}

export interface OrchestratorAgentPrompt {
  agentId: string;
  systemPrompt: string;
  version: number;
  updatedAt: string;
  updatedByUsuarioId?: string;
  examplesPositive?: string[];
  examplesNegative?: string[];
  threshold?: number;
  blocks?: SdrBlock[];
}

export interface OrchestratorPromptPack {
  organizationId: string;
  agents: OrchestratorAgentPrompt[];
  updatedAt: string;
}

// IA Console SDR Conversation Builder (OpenSpec #2.5)
export interface SdrBlock {
  id: string;
  title: string;
  level: 1 | 2 | 3;
  description?: string;
  body: string;
  icon?: string;
  order: number;
}

export type SdrBlocksVersion = `v${number}.${number}` | 'v0';

export interface SdrConversationBlocksPack {
  organizationId: string;
  agentId: 'sdr-conversation';
  blocks: SdrBlock[];
  systemPromptDerived: string;
  version: SdrBlocksVersion;
  updatedAt: string;
  updatedBy?: string;
}

// Histórico — version archive of SDR Conversation blocks
export interface SdrConversationVersionSummary {
  versionId: string;
  version: SdrBlocksVersion;
  createdAt: string;
  createdBy?: string;
  note?: string;
  blocksCount: number;
  totalBytes: number;
}

export interface SdrConversationVersionsList {
  organizationId: string;
  versions: SdrConversationVersionSummary[];
  currentVersion: SdrBlocksVersion;
}

export interface SdrConversationVersion {
  organizationId: string;
  versionId: string;
  version: SdrBlocksVersion;
  agentId: 'sdr-conversation';
  blocks: SdrBlock[];
  systemPromptDerived: string;
  createdAt: string;
  createdBy?: string;
  note?: string;
  expiresAt: number;
}

// Proposal fields config (SDR · Campos tab, OpenSpec #5)
export type ProposalFieldSource = 'manual' | 'ai';

export interface ProposalField {
  id: string;
  key: string;
  label: string;
  required: boolean;
  conditional: string | null;
  validation?: string;
  prompt: string;
  example?: string;
  source: ProposalFieldSource;
  order: number;
}

export interface ProposalFieldsConfig {
  organizationId: string;
  objective: string;
  fields: ProposalField[];
  rules: string[];
  forbidden: string[];
  completion: string;
  version: SdrBlocksVersion;
  updatedAt: string;
  updatedBy?: string;
}

export interface PromptCatalogItem {
  promptId: string;
  title: string;
  description: string;
  defaultReadonly: string;
  overridePrompt: string;
  mergedPreview: string;
  outputSchema?: string;
  examplesPositive?: string[];
  examplesNegative?: string[];
  threshold?: number;
  /** Quando false, override per-tenant é bloqueado; só super-admin edita via baseline yaml. */
  tenantOverridable?: boolean;
}

/** Resposta de GET /orchestrator-prompts/catalog (prompts + dicas de UI servidas pelo backend). */
export interface OrchestratorPromptCatalogAdminHints {
  profileDomainExample: string;
}

export interface OrchestratorPromptCatalogResponse {
  prompts: PromptCatalogItem[];
  adminHints: OrchestratorPromptCatalogAdminHints;
}

export type SpecialistPromptScope = 'global' | 'tenant';

export type SpecialistReturnContract =
  | 'negociacao'
  | 'proposta'
  | 'handoff_humano'
  | 'toxico'
  | 'low_intent_non_actionable'
  | 'explicit_disinterest'
  | 'opt_out'
  | 'reativacao_intent';

export interface SpecialistPrompt {
  specialistId: string;
  name: string;
  description: string;
  promptText: string;
  returnContract: SpecialistReturnContract;
  scope: SpecialistPromptScope;
  organizationId?: string;
  isActive: boolean;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdByUsuarioId: string;
  updatedByUsuarioId: string;
  isNative?: boolean;
  /** Endpoint runtime pode marcar readonly para perfis da org. */
  readonly?: boolean;
}
